import React, { useState, useEffect } from 'react';
import {
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CurrencyPoundIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ChartBarIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import { schedulesAPI, timeAttendanceAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const UKComplianceDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    complianceOverview: {
      totalViolations: 0,
      criticalViolations: 0,
      warningViolations: 0,
      complianceScore: 100
    },
    workingTimeCompliance: {
      dailyRestViolations: 0,
      weeklyRestViolations: 0,
      fortyEightHourViolations: 0,
      breakViolations: 0
    },
    payrollCompliance: {
      nmwViolations: 0,
      pensionEnrolmentDue: 0,
      holidayPayIssues: 0
    },
    recentViolations: [],
    upcomingDeadlines: []
  });

  const { user, isAdmin, isHRManager } = useAuth();

  useEffect(() => {
    if (isAdmin() || isHRManager()) {
      fetchComplianceData();
    }
  }, []);

  const fetchComplianceData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [violationsResponse, reportResponse] = await Promise.all([
        schedulesAPI.getComplianceViolations({ 
          limit: 10,
          status: 'active',
          period: 'current_month'
        }),
        timeAttendanceAPI.getComplianceReport({
          start_date: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
          end_date: new Date().toISOString().split('T')[0]
        })
      ]);

      // Process violations data
      const violations = violationsResponse.data.violations || [];
      const workingTimeViolations = violations.filter(v => v.rule_category === 'WTR');
      const payrollViolations = violations.filter(v => ['NMW', 'PENSION', 'HOLIDAY'].includes(v.rule_category));

      setDashboardData({
        complianceOverview: {
          totalViolations: violations.length,
          criticalViolations: violations.filter(v => v.severity === 'error').length,
          warningViolations: violations.filter(v => v.severity === 'warning').length,
          complianceScore: Math.max(0, 100 - (violations.length * 2))
        },
        workingTimeCompliance: {
          dailyRestViolations: workingTimeViolations.filter(v => v.rule_code === 'WTR_DAILY_REST').length,
          weeklyRestViolations: workingTimeViolations.filter(v => v.rule_code === 'WTR_WEEKLY_REST').length,
          fortyEightHourViolations: workingTimeViolations.filter(v => v.rule_code === 'WTR_48HR_AVERAGE').length,
          breakViolations: workingTimeViolations.filter(v => v.rule_code === 'WTR_REST_BREAKS').length
        },
        payrollCompliance: {
          nmwViolations: payrollViolations.filter(v => v.rule_category === 'NMW').length,
          pensionEnrolmentDue: payrollViolations.filter(v => v.rule_category === 'PENSION').length,
          holidayPayIssues: payrollViolations.filter(v => v.rule_category === 'HOLIDAY').length
        },
        recentViolations: violations.slice(0, 5),
        upcomingDeadlines: [] // This would come from a dedicated endpoint
      });

    } catch (error) {
      console.error('Error fetching compliance data:', error);
      setError('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  };

  const getComplianceScoreColor = (score) => {
    if (score >= 95) return 'text-green-600';
    if (score >= 85) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getSeverityColor = (severity) => {
    const colors = {
      error: 'bg-red-100 text-red-800',
      warning: 'bg-yellow-100 text-yellow-800',
      info: 'bg-blue-100 text-blue-800'
    };
    return colors[severity] || colors.info;
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAdmin() && !isHRManager()) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ShieldCheckIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-gray-900">Access Denied</h1>
          <p className="mt-2 text-gray-600">You don't have permission to access the compliance dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">UK Compliance Dashboard</h1>
          <p className="text-sm text-gray-600 mt-1">Monitor employment law compliance and violations</p>
        </div>
        <button
          onClick={fetchComplianceData}
          disabled={loading}
          className="btn-secondary"
        >
          {loading ? 'Refreshing...' : 'Refresh Data'}
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="card">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <ShieldCheckIcon className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Compliance Score</p>
                  <p className={`text-2xl font-semibold ${getComplianceScoreColor(dashboardData.complianceOverview.complianceScore)}`}>
                    {dashboardData.complianceOverview.complianceScore}%
                  </p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 rounded-lg">
                  <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Critical Violations</p>
                  <p className="text-2xl font-semibold text-red-600">
                    {dashboardData.complianceOverview.criticalViolations}
                  </p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Warnings</p>
                  <p className="text-2xl font-semibold text-yellow-600">
                    {dashboardData.complianceOverview.warningViolations}
                  </p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <DocumentTextIcon className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Issues</p>
                  <p className="text-2xl font-semibold text-blue-600">
                    {dashboardData.complianceOverview.totalViolations}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Working Time Regulations */}
          <div className="card">
            <div className="flex items-center mb-6">
              <ClockIcon className="w-6 h-6 text-blue-600 mr-3" />
              <h2 className="text-xl font-semibold text-gray-900">Working Time Regulations</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600 mb-2">
                  {dashboardData.workingTimeCompliance.dailyRestViolations}
                </div>
                <div className="text-sm text-gray-600">11-Hour Rest Violations</div>
              </div>

              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600 mb-2">
                  {dashboardData.workingTimeCompliance.weeklyRestViolations}
                </div>
                <div className="text-sm text-gray-600">Weekly Rest Violations</div>
              </div>

              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600 mb-2">
                  {dashboardData.workingTimeCompliance.fortyEightHourViolations}
                </div>
                <div className="text-sm text-gray-600">48-Hour Average Violations</div>
              </div>

              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600 mb-2">
                  {dashboardData.workingTimeCompliance.breakViolations}
                </div>
                <div className="text-sm text-gray-600">Break Violations</div>
              </div>
            </div>
          </div>

          {/* Payroll Compliance */}
          <div className="card">
            <div className="flex items-center mb-6">
              <CurrencyPoundIcon className="w-6 h-6 text-green-600 mr-3" />
              <h2 className="text-xl font-semibold text-gray-900">Payroll & Benefits Compliance</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600 mb-2">
                  {dashboardData.payrollCompliance.nmwViolations}
                </div>
                <div className="text-sm text-gray-600">NMW Violations</div>
              </div>

              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600 mb-2">
                  {dashboardData.payrollCompliance.pensionEnrolmentDue}
                </div>
                <div className="text-sm text-gray-600">Pension Enrolments Due</div>
              </div>

              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600 mb-2">
                  {dashboardData.payrollCompliance.holidayPayIssues}
                </div>
                <div className="text-sm text-gray-600">Holiday Pay Issues</div>
              </div>
            </div>
          </div>

          {/* Recent Violations */}
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Recent Compliance Issues</h2>
              <button 
                onClick={() => window.location.href = '/compliance-violations'}
                className="btn-secondary text-sm"
              >
                View All
              </button>
            </div>

            {dashboardData.recentViolations.length === 0 ? (
              <div className="text-center py-8">
                <ShieldCheckIcon className="w-12 h-12 text-green-400 mx-auto mb-4" />
                <p className="text-gray-600">No recent compliance violations found.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {dashboardData.recentViolations.map((violation, index) => (
                  <div key={index} className="flex items-start p-4 bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0 mr-3">
                      <ExclamationTriangleIcon 
                        className={`w-5 h-5 ${
                          violation.severity === 'error' ? 'text-red-500' : 'text-yellow-500'
                        }`} 
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-medium text-gray-900">
                          {violation.rule_name}
                        </p>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSeverityColor(violation.severity)}`}>
                          {violation.severity}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        {violation.error_message}
                      </p>
                      <div className="flex items-center text-xs text-gray-500">
                        <span>Employee: {violation.employee_name}</span>
                        <span className="mx-2">•</span>
                        <span>{formatDateTime(violation.detected_at)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="card">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Quick Actions</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <button 
                onClick={() => window.location.href = '/uk-scheduling'}
                className="flex items-center p-4 text-left bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
              >
                <CalendarDaysIcon className="w-6 h-6 text-blue-600 mr-3" />
                <div>
                  <div className="font-medium text-blue-900">Manage Rotas</div>
                  <div className="text-sm text-blue-600">Schedule with compliance</div>
                </div>
              </button>

              <button 
                onClick={() => window.location.href = '/time-attendance'}
                className="flex items-center p-4 text-left bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
              >
                <ClockIcon className="w-6 h-6 text-green-600 mr-3" />
                <div>
                  <div className="font-medium text-green-900">Time & Attendance</div>
                  <div className="text-sm text-green-600">View timesheets</div>
                </div>
              </button>

              <button 
                onClick={() => window.location.href = '/compliance-reports'}
                className="flex items-center p-4 text-left bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
              >
                <ChartBarIcon className="w-6 h-6 text-purple-600 mr-3" />
                <div>
                  <div className="font-medium text-purple-900">Compliance Reports</div>
                  <div className="text-sm text-purple-600">Generate reports</div>
                </div>
              </button>

              <button 
                onClick={() => window.location.href = '/payroll'}
                className="flex items-center p-4 text-left bg-yellow-50 hover:bg-yellow-100 rounded-lg transition-colors"
              >
                <CurrencyPoundIcon className="w-6 h-6 text-yellow-600 mr-3" />
                <div>
                  <div className="font-medium text-yellow-900">Payroll Processing</div>
                  <div className="text-sm text-yellow-600">Process payroll</div>
                </div>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UKComplianceDashboard;